/**
 * BattleReportDialog — Two-column post-combat summary.
 * Shown when an engagement involving the player's units ends.
 */

import Phaser from 'phaser';
import {
  Colors, CSSColors, Fonts, FontSize, createDialogChrome,
} from './GameUITheme';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';

// ── Accumulator interfaces ─────────────────────────────────────

export interface CombatSideStats {
  totalDamage: number;
  totalShieldDamage: number;
  totalHullDamage: number;
  weaponsFired: number;
  hits: number;
  misses: number;
  subsystemHits: { engines: number; weapons: number; shields: number };
  weaponBreakdown: Map<string, { fired: number; hits: number; damage: number }>;
  finalHp: number;
  destroyed: boolean;
}

export interface CombatAccumulator {
  attackerName: string;
  defenderName: string;
  attackerId: string;
  defenderId: string;
  attackerStats: CombatSideStats;
  defenderStats: CombatSideStats;
  rounds: number;
}

export function createEmptySideStats(): CombatSideStats {
  return {
    totalDamage: 0,
    totalShieldDamage: 0,
    totalHullDamage: 0,
    weaponsFired: 0,
    hits: 0,
    misses: 0,
    subsystemHits: { engines: 0, weapons: 0, shields: 0 },
    weaponBreakdown: new Map(),
    finalHp: 0,
    destroyed: false,
  };
}

export function accumulateCombatResult(acc: CombatAccumulator, result: any): void {
  acc.rounds++;

  // Accumulate attacker's weapon events (attacker fires at defender)
  for (const evt of (result.attackerEvents || [])) {
    const stats = acc.attackerStats;
    stats.weaponsFired++;
    if (evt.hit) {
      stats.hits++;
      stats.totalDamage += evt.damage || 0;
      stats.totalShieldDamage += evt.shieldDamage || 0;
      stats.totalHullDamage += evt.hullDamage || 0;
      if (evt.subsystemHit) {
        const key = evt.subsystemHit.toLowerCase() as keyof typeof stats.subsystemHits;
        if (key in stats.subsystemHits) stats.subsystemHits[key]++;
      }
    } else {
      stats.misses++;
    }
    // Per-weapon breakdown
    const wName = evt.weaponUsed || 'Unknown';
    let wb = stats.weaponBreakdown.get(wName);
    if (!wb) { wb = { fired: 0, hits: 0, damage: 0 }; stats.weaponBreakdown.set(wName, wb); }
    wb.fired++;
    if (evt.hit) { wb.hits++; wb.damage += evt.damage || 0; }
  }

  // Accumulate defender's weapon events (defender fires at attacker)
  for (const evt of (result.defenderEvents || [])) {
    const stats = acc.defenderStats;
    stats.weaponsFired++;
    if (evt.hit) {
      stats.hits++;
      stats.totalDamage += evt.damage || 0;
      stats.totalShieldDamage += evt.shieldDamage || 0;
      stats.totalHullDamage += evt.hullDamage || 0;
      if (evt.subsystemHit) {
        const key = evt.subsystemHit.toLowerCase() as keyof typeof stats.subsystemHits;
        if (key in stats.subsystemHits) stats.subsystemHits[key]++;
      }
    } else {
      stats.misses++;
    }
    const wName = evt.weaponUsed || 'Unknown';
    let wb = stats.weaponBreakdown.get(wName);
    if (!wb) { wb = { fired: 0, hits: 0, damage: 0 }; stats.weaponBreakdown.set(wName, wb); }
    wb.fired++;
    if (evt.hit) { wb.hits++; wb.damage += evt.damage || 0; }
  }

  // Track final HP and destruction status
  acc.attackerStats.finalHp = result.attackerHp ?? acc.attackerStats.finalHp;
  acc.defenderStats.finalHp = result.defenderHp ?? acc.defenderStats.finalHp;
  if (result.attackerDestroyed) acc.attackerStats.destroyed = true;
  if (result.defenderDestroyed) acc.defenderStats.destroyed = true;
}

// ── Dialog ─────────────────────────────────────────────────────

const COL_LEFT_X = 30;
const COL_RIGHT_X = 220;
const ROW_START_Y = 70;
const ROW_HEIGHT = 22;

export class BattleReportDialog extends Phaser.GameObjects.Container {
  private dw: number;
  private dh: number;
  private autoDismissTimer?: Phaser.Time.TimerEvent;

  constructor(scene: Phaser.Scene, acc: CombatAccumulator) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 420;
    const dh = mobile ? 480 : 380;
    const { width, height } = scene.cameras.main;
    const cx = Math.round((width - dw) / 2);
    const cy = Math.round((height - dh) / 2);
    super(scene, cx, cy);
    this.dw = dw;
    this.dh = dh;
    scene.add.existing(this);
    this.setDepth(200);

    const close = () => {
      if (this.autoDismissTimer) this.autoDismissTimer.destroy();
      this.destroy();
    };

    createDialogChrome(scene, this, this.dw, this.dh, {
      onClose: close,
      title: I18n.t('dialog.battle_report'),
    });

    this.buildContent(acc);

    // Auto-dismiss after 10 seconds
    this.autoDismissTimer = scene.time.delayedCall(10000, close);
  }

  private buildContent(acc: CombatAccumulator): void {
    const scene = this.scene;
    let y = ROW_START_Y;

    // Column headers (unit names)
    const leftName = this.ellipsis(acc.attackerName, 16);
    const rightName = this.ellipsis(acc.defenderName, 16);

    this.addText(COL_LEFT_X, y, leftName, CSSColors.red, FontSize.sm, true);
    this.addText(COL_RIGHT_X, y, rightName, CSSColors.cyan, FontSize.sm, true);
    y += ROW_HEIGHT + 4;

    // Separator
    const sep = scene.add.graphics();
    sep.lineStyle(1, Colors.cyan, 0.12);
    sep.lineBetween(20, y, this.dw - 20, y);
    this.add(sep);
    y += 8;

    // Stats rows
    const rows: [string, string, string][] = [
      ['Damage Dealt', String(acc.attackerStats.totalDamage), String(acc.defenderStats.totalDamage)],
      ['Hits / Misses', `${acc.attackerStats.hits}/${acc.attackerStats.misses}`, `${acc.defenderStats.hits}/${acc.defenderStats.misses}`],
      ['Shields Absorbed', String(acc.attackerStats.totalShieldDamage), String(acc.defenderStats.totalShieldDamage)],
      ['Hull Damage', String(acc.attackerStats.totalHullDamage), String(acc.defenderStats.totalHullDamage)],
      ['Subsystem Hits', this.formatSubsystem(acc.attackerStats), this.formatSubsystem(acc.defenderStats)],
      ['Final HP', acc.attackerStats.destroyed ? 'DESTROYED' : String(acc.attackerStats.finalHp), acc.defenderStats.destroyed ? 'DESTROYED' : String(acc.defenderStats.finalHp)],
      ['Rounds', String(acc.rounds), ''],
    ];

    for (const [label, left, right] of rows) {
      this.addText(COL_LEFT_X, y, label, CSSColors.textMuted, '12px');
      this.addText(COL_LEFT_X + 120, y, left, CSSColors.textWhite, '12px');
      if (right) this.addText(COL_RIGHT_X + 100, y, right, CSSColors.textWhite, '12px');
      y += ROW_HEIGHT;
    }

    y += 8;

    // Per-weapon breakdown header
    const sep2 = scene.add.graphics();
    sep2.lineStyle(1, Colors.cyan, 0.12);
    sep2.lineBetween(20, y, this.dw - 20, y);
    this.add(sep2);
    y += 6;

    this.addText(COL_LEFT_X, y, 'WEAPON BREAKDOWN', CSSColors.textMuted, '11px', true);
    y += 18;

    // Attacker weapons
    for (const [wName, wb] of acc.attackerStats.weaponBreakdown) {
      const short = this.ellipsis(wName, 14);
      this.addText(COL_LEFT_X, y, `${short}: ${wb.hits}/${wb.fired} → ${wb.damage} dmg`, '#ff6666', '11px');
      y += 16;
    }

    // Defender weapons
    for (const [wName, wb] of acc.defenderStats.weaponBreakdown) {
      const short = this.ellipsis(wName, 14);
      this.addText(COL_LEFT_X, y, `${short}: ${wb.hits}/${wb.fired} → ${wb.damage} dmg`, '#6666ff', '11px');
      y += 16;
    }

    // Result banner at bottom
    let resultText: string;
    let resultColor: string;
    if (acc.attackerStats.destroyed && acc.defenderStats.destroyed) {
      resultText = 'MUTUAL DESTRUCTION';
      resultColor = '#ff4444';
    } else if (acc.attackerStats.destroyed) {
      resultText = `${acc.defenderName} WINS`;
      resultColor = CSSColors.cyan;
    } else if (acc.defenderStats.destroyed) {
      resultText = `${acc.attackerName} WINS`;
      resultColor = CSSColors.red;
    } else {
      resultText = 'DISENGAGED';
      resultColor = CSSColors.textMuted;
    }

    this.addText(this.dw / 2, this.dh - 30, resultText, resultColor, FontSize.md, true)
      .setOrigin(0.5);
  }

  private addText(
    x: number, y: number, content: string, color: string,
    size: string = '12px', bold: boolean = false,
  ): Phaser.GameObjects.Text {
    const text = this.scene.add.text(x, y, content, {
      fontSize: size,
      color,
      fontFamily: bold ? Fonts.heading : Fonts.body,
    });
    this.add(text);
    return text;
  }

  private formatSubsystem(stats: CombatSideStats): string {
    const parts: string[] = [];
    if (stats.subsystemHits.engines > 0) parts.push(`E:${stats.subsystemHits.engines}`);
    if (stats.subsystemHits.weapons > 0) parts.push(`W:${stats.subsystemHits.weapons}`);
    if (stats.subsystemHits.shields > 0) parts.push(`S:${stats.subsystemHits.shields}`);
    return parts.length > 0 ? parts.join(' ') : '—';
  }

  private ellipsis(s: string, max: number): string {
    return s.length > max ? s.slice(0, max - 1) + '…' : s;
  }
}
